import { Drawer, DrawerAction, DrawerBody, DrawerHeader } from '@/components/Drawer';
import { Input } from '@tigergraph/app-ui-lib/input';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Button } from '@tigergraph/app-ui-lib/button';
import { KIND } from 'baseui/button';
import { useMutation, useQuery } from 'react-query';
import { axiosController } from '@/lib/network';
import { useEffect, useState } from 'react';
import { Block } from 'baseui/block';
import useCopyClipboard from 'react-use-clipboard';
import { CopyIcon } from 'lucide-react';
import { Body2 } from '@tigergraph/app-ui-lib/typography';
import { ProgressBar } from 'baseui/progress-bar';
import { showToast } from '@/components/styledToasterContainer';
import { getErrorMessage, validateBackupTag } from '@/utils/utils';
import { AxiosError } from 'axios';

export interface BackupDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  workgroupId: string;
  workspaceId: string;
  onBackupSuccess?: () => void;
}

export enum BackupStatus {
  Active = 'Active',
  Succeed = 'Succeed',
  Failed = 'Failed',
}

export function BackupDrawer({ isOpen, onClose, workgroupId, workspaceId, onBackupSuccess }: BackupDrawerProps) {
  const [css, theme] = useStyletron();
  const [backupName, setBackupName] = useState('');
  const [backupId, setBackupId] = useState('');
  const [backupNameCopied, setBackupNameCopied] = useCopyClipboard(backupId, {
    successDuration: 1000,
  });
  const [backupLogs, setBackupLogs] = useState('');
  const [progress, setProgress] = useState(0);
  const [backupFailed, setBackupFailed] = useState(false);
  const [intervalTimer, setIntervalTimer] = useState<number | null>(null);
  const [isBackupNameValid, setIsBackupNameValid] = useState(true);

  // estimate backup time is 180 seconds
  const maxEstimateProgress = 180;

  const { mutate, isLoading } = useMutation(
    ['createBackup'],
    async (name: string) => {
      const response = await axiosController.post(`/v2/workgroups/${workgroupId}/workspaces/${workspaceId}/backups`, {
        name,
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        setBackupId(data.Result);
      },
      onError(error) {
        showToast({
          kind: 'negative',
          message: `${getErrorMessage(error as AxiosError)}`,
        });
      },
    }
  );

  const handleClose = () => {
    onClose && onClose();
  };

  useEffect(() => {
    if (backupId) {
      // fake progress
      // @ts-ignore
      const interval: number = setInterval(() => {
        setProgress((prev) => {
          if (prev < maxEstimateProgress - 1) {
            return prev + 1;
          } else {
            return prev;
          }
        });
      }, 1000);
      setIntervalTimer(interval);
      return () => clearInterval(interval);
    }
  }, [backupFailed, backupId]);

  useEffect(() => {
    if (backupFailed) {
      intervalTimer && clearInterval(intervalTimer as number);
    }
  }, [backupFailed, intervalTimer]);

  useEffect(() => {
    if (backupNameCopied) {
      showToast({
        kind: 'positive',
        message: 'tag copied successfully.',
      });
    }
  }, [backupNameCopied]);

  useQuery(
    'queryBackupStatus',
    async () => {
      const response = await axiosController.get(
        `/v2/workgroups/${workgroupId}/workspaces/${workspaceId}/backups/${backupId}/status`
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (data.Result.status === BackupStatus.Succeed) {
          onBackupSuccess && onBackupSuccess();
        }
        if (data.Result.status === BackupStatus.Failed) {
          setBackupFailed(true);
          showToast({
            kind: 'negative',
            message: 'Backup failed for some reasons.',
          });
        }
        setBackupLogs(data.Result.output);
      },
      refetchInterval: 5000,
      enabled: progress !== maxEstimateProgress && !!backupId && !backupFailed,
    }
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      overrides={{
        DrawerBody: {
          style: {
            overflowY: 'hidden',
          },
        },
      }}
    >
      <DrawerHeader>Backup</DrawerHeader>
      <DrawerBody
        $style={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 121px)',
        }}
      >
        <div>
          TigerGraph Cloud provides you with a manual backup option in addition to the auto-scheduled backups. While
          auto-scheduled backups ensure regular data protection, manual backups allow you to create backups on-demand,
          giving you more control over when and how often you back up your data. Once the backup is started, you can
          also view the status and details of your manual backups, making it easy to keep track of your backup progress.
          Manual backups are especially useful when you need to capture a snapshot of your data at a specific point in
          time or before making significant changes.
        </div>
        <div
          className={css({
            borderBottom: '1px solid #E8E8E8',
            margin: '16px -10px',
            width: 'calc(100% + 20px)',
          })}
        ></div>
        <div>Backup Tag</div>
        {backupId ? (
          <>
            <Block display={'flex'} alignItems={'center'} marginTop={'8px'}>
              {backupName}
              <CopyIcon
                style={{ marginLeft: '8px' }}
                color={'#3F5870'}
                size={14}
                onClick={() => {
                  setBackupNameCopied();
                }}
              />
            </Block>
            <Block display={'flex'} marginTop={'16px'} flexDirection={'column'}>
              <Body2>Backup Progress</Body2>
              <Block display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <ProgressBar
                  overrides={{
                    Root: {
                      style: {
                        width: '60%',
                        flexGrow: 1,
                      },
                    },
                    BarContainer: {
                      style: {
                        marginLeft: '0',
                        marginRight: '0',
                      },
                    },
                  }}
                  value={progress}
                  maxValue={maxEstimateProgress}
                />
                <span
                  className={css({
                    marginLeft: '16px',
                  })}
                >
                  Estimate: 2 mins
                </span>
              </Block>
            </Block>
            <Body2 marginBottom={'8px'} marginTop={'16px'}>
              Backup Logs
            </Body2>
            <div
              className={css({
                whiteSpace: 'pre-line',
                fontSize: '12px',
                fontFamily: 'Monaco',
                color: `${theme.colors.black01}`,
                padding: '10px',
                backgroundColor: `${theme.colors.gray50}`,
                borderRadius: '4px',
                overflowY: 'auto',
              })}
            >
              {backupLogs}
            </div>
          </>
        ) : (
          <>
            <Input
              autoFocus
              value={backupName}
              onChange={(e) => {
                setBackupName(e.currentTarget.value);
                validateBackupTag(e.currentTarget.value) && setIsBackupNameValid(true);
              }}
              overrides={{
                Root: {
                  style: {
                    margin: '8px 0',
                  },
                },
              }}
            />
            {!isBackupNameValid && (
              <div
                className={css({
                  color: theme.colors.negative,
                })}
              >
                Please ensure that the string only contains characters ([a-zA-Z0-9]), hyphens (-), and underscores (_).
              </div>
            )}
          </>
        )}
      </DrawerBody>
      <DrawerAction>
        <Button kind={KIND.tertiary} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          disabled={backupId !== '' || isLoading}
          onClick={() => {
            if (validateBackupTag(backupName)) {
              mutate(backupName);
            } else {
              setIsBackupNameValid(false);
            }
          }}
        >
          Backup
        </Button>
      </DrawerAction>
    </Drawer>
  );
}
